import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { loadMasters } from '../../../modules/masters/services/masterService';
import { cn } from '../../../utils/cn';
import { Portal } from '../../ui/Portal/Portal';
import { BottomNav } from '../BottomNav/BottomNav';
import { BreadcrumbProvider } from '../Breadcrumbs/Breadcrumbs';
import { Header } from '../Header/Header';
import { PageErrorBoundary } from '../PageErrorBoundary/PageErrorBoundary';
import { Sidebar } from '../Sidebar/Sidebar';

const COLLAPSE_KEY = 'electroz.sidebarCollapsed';

function readCollapsed() {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

/** App shell: sidebar, header, the current page and the phone bottom bar. */
export function AppLayout() {
  const [drawer, setDrawer] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const { pathname } = useLocation();

  useEffect(() => setDrawer(false), [pathname]);
  // Dropdown lists used across the app; loaded once after login.
  useEffect(() => {
    loadMasters().catch(() => {});
  }, []);
  useEffect(() => {
    if (!drawer) return undefined;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setDrawer(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawer]);

  const toggleSidebar = () =>
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, c ? '0' : '1');
      } catch {
        /* storage blocked: the choice just won't be remembered */
      }
      return !c;
    });

  return (
    <BreadcrumbProvider>
      <div className={cn('min-h-screen transition-[padding] duration-200', collapsed ? 'lg:pl-[72px]' : 'lg:pl-64')}>
        <Sidebar className="fixed inset-y-0 left-0 z-30 hidden lg:flex" collapsed={collapsed} />

        {drawer && (
          <Portal>
            <div className="fixed inset-0 z-40 lg:hidden">
              <div className="absolute inset-0 bg-navy-900/50 animate-fade" onClick={() => setDrawer(false)} aria-hidden="true" />
              <Sidebar className="relative animate-drawer shadow-pop" onClose={() => setDrawer(false)} onNavigate={() => setDrawer(false)} />
            </div>
          </Portal>
        )}

        <Header onMenu={() => setDrawer(true)} onToggleSidebar={toggleSidebar} collapsed={collapsed} />
        <main className="w-full px-4 pb-28 pt-6 sm:px-6 lg:px-8 lg:pb-12 lg:pt-8">
          <PageErrorBoundary key={pathname}>
            <Outlet />
          </PageErrorBoundary>
        </main>
        <BottomNav onMore={() => setDrawer(true)} />
      </div>
    </BreadcrumbProvider>
  );
}

export default AppLayout;
